import type { KycDecisionRow, KycReview } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { documentLabel } from '@/components/kyc-detail';
import { PageHeader } from '@/components/page-header';
import { StatusPill } from '@/components/primitives/status-pill';
import {
  OpenRecord,
  PersonCell,
  SummaryBand,
} from '@/components/queue/queue-parts';
import { SlaChip } from '@/components/queue/sla-chip';
import { useSheet, useSheetActions } from '@/components/sheet/use-sheet';
import { TabLinks } from '@/components/tab-links';
import { AttemptsLog } from '@/components/verifications/attempts-log';
import { formatDateTime, relativeTime } from '@/lib/format';
import { hasCapability } from '@/lib/market';
import { adminQuery } from '@/lib/query';
import { ageTone, duration } from '@/lib/sla';

const searchSchema = z.object({
  tab: z
    .enum(['review', 'decisions', 'attempts'])
    .catch('review')
    .default('review'),
});

export const Route = createFileRoute('/_app/verifications')({
  validateSearch: searchSchema,
  component: VerificationsPage,
});

const POLL_MS = 30_000;

const PURPOSE: Record<KycReview['purpose'], string> = {
  registration: 'Registration',
  reauth: 'PIN reset',
};

/** A queue row keyed by its session, so the table keeps a row's identity across refreshes. */
type ReviewRow = KycReview & { id: string };

const review = createColumnHelper<ReviewRow>();

const REVIEW_COLUMNS = [
  review.accessor('in_review_since', {
    header: 'Waiting',
    cell: (info) => <SlaChip kind="age" at={info.getValue()} />,
    meta: { sortValue: (row) => Date.parse(row.in_review_since) },
  }),
  review.display({
    id: 'customer',
    header: 'Customer',
    meta: { sortValue: (row) => row.customer?.name ?? '' },
    cell: ({ row }) => (
      <span className="flex flex-col gap-0.5">
        <OpenRecord
          sheetRef={{ type: 'kyc', id: row.original.session_id }}
          label={`Review ${row.original.customer?.name ?? 'this'} ID check`}
          className="max-w-full hover:underline"
        >
          <PersonCell customer={row.original.customer} />
        </OpenRecord>
        {row.original.last_refusal ? (
          <span className="text-xs text-danger-ink">
            The last decision was refused: {row.original.last_refusal}
          </span>
        ) : null}
      </span>
    ),
  }),
  review.accessor((row) => PURPOSE[row.purpose], {
    id: 'purpose',
    header: 'Purpose',
  }),
  review.accessor(
    (row) => documentLabel(row.document_type, row.issuing_country),
    { id: 'document', header: 'Document' },
  ),
  review.accessor('started_at', {
    header: 'Started',
    cell: (info) => (
      <span className="text-muted-foreground">
        {relativeTime(info.getValue())}
      </span>
    ),
  }),
  review.display({
    id: 'state',
    header: 'State',
    cell: ({ row }) =>
      row.original.deciding ? (
        <StatusPill tone="info" label="Sending decision" />
      ) : null,
  }),
];

const decided = createColumnHelper<KycDecisionRow>();

const DECISION_COLUMNS = [
  decided.accessor('requested_at', {
    header: 'Decided',
    cell: (info) => (
      <span title={formatDateTime(info.getValue())}>
        {relativeTime(info.getValue())}
      </span>
    ),
  }),
  decided.display({
    id: 'customer',
    header: 'Customer',
    meta: { sortValue: (row) => row.customer?.name ?? '' },
    cell: ({ row }) => <PersonCell customer={row.original.customer} />,
  }),
  decided.accessor('new_status', {
    header: 'Decision',
    cell: (info) => (
      <StatusPill
        tone={info.getValue() === 'Approved' ? 'success' : 'danger'}
        label={info.getValue()}
      />
    ),
  }),
  decided.accessor('reason', {
    header: 'Reason',
    cell: (info) => (
      <span className="line-clamp-2 max-w-sm">{info.getValue()}</span>
    ),
  }),
  decided.accessor('staff', { header: 'By' }),
  decided.accessor(
    (row) => {
      if (row.result === 'sent') return 'Sent';
      if (row.result === 'refused')
        return `Refused: ${row.result_detail ?? ''}`;
      return 'Sending…';
    },
    { id: 'sent', header: 'Sent to provider' },
  ),
  decided.accessor('session_status', {
    header: 'Session now',
    cell: (info) => <StatusPill status={info.getValue()} />,
  }),
];

/** ID checks that need a person, decided here instead of on the provider's dashboard. */
function VerificationsPage() {
  const search = Route.useSearch();
  // The check log is the Ghana Card register's (Margins/NIA); a cell without one has none.
  const hasLog = hasCapability('id_register');
  const tab = search.tab === 'attempts' && !hasLog ? 'review' : search.tab;
  // A refresh must not move rows under a cursor or a hand about to press one.
  const [engaged, setEngaged] = useState(false);
  const sheetOpen = useSheet().top !== undefined;
  const list = useQuery({
    ...adminQuery('kyc.list', {}),
    refetchInterval: engaged || sheetOpen ? false : POLL_MS,
    select: (data) => ({
      ...data,
      rows: data.rows.map((row): ReviewRow => ({ ...row, id: row.session_id })),
    }),
  });
  const waiting = list.data?.rows.length;
  const oldest = list.data?.rows[0]
    ? Date.now() - Date.parse(list.data.rows[0].in_review_since)
    : null;
  const today = new Date().toISOString().slice(0, 10);
  const decidedToday = list.data?.recent.filter((row) =>
    row.requested_at.startsWith(today),
  ).length;

  return (
    <>
      <PageHeader
        title="Verifications"
        description={
          hasLog
            ? 'ID checks that need a person (oldest first), the decisions made here, and a log of every Ghana Card check attempt.'
            : 'ID checks that need a person (oldest first) and the decisions made here.'
        }
      />
      <SummaryBand
        label="Verification figures"
        cells={[
          { label: 'Waiting', value: waiting },
          {
            label: 'Oldest',
            value: oldestText(waiting, oldest),
            tone:
              oldest !== null && ageTone(oldest) !== 'neutral'
                ? ageTone(oldest)
                : undefined,
          },
          { label: 'Decided today', value: decidedToday },
        ]}
      />
      <TabLinks
        tabs={[
          {
            key: 'review',
            label: waiting === undefined ? 'Review' : `Review (${waiting})`,
          },
          { key: 'decisions', label: 'Decisions' },
          ...(hasLog ? [{ key: 'attempts', label: 'Check log' }] : []),
        ]}
        active={tab}
        render={(item, domProps) => (
          <Link
            {...domProps}
            to="/verifications"
            search={{ tab: item.key as 'review' | 'decisions' | 'attempts' }}
          />
        )}
      />
      {tab === 'attempts' ? <AttemptsLog /> : null}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: only tracks where the pointer and focus are, to pause refreshing. */}
      <div
        hidden={tab === 'attempts'}
        onPointerEnter={() => setEngaged(true)}
        onPointerLeave={() => setEngaged(false)}
        onFocus={() => setEngaged(true)}
        onBlur={() => setEngaged(false)}
      >
        <QueryState
          query={list}
          label="verifications"
          staleAfterMs={3 * POLL_MS}
          skeleton={<SkeletonTable columns={7} />}
        >
          {(data) => (
            <TabBody
              tab={tab === 'decisions' ? 'decisions' : 'review'}
              data={data}
            />
          )}
        </QueryState>
      </div>
    </>
  );
}

function oldestText(
  waiting: number | undefined,
  oldest: number | null,
): string | undefined {
  if (waiting === 0) return 'None';
  return oldest === null ? undefined : duration(oldest);
}

function TabBody({
  tab,
  data,
}: Readonly<{
  tab: 'review' | 'decisions';
  data: { rows: ReviewRow[]; recent: KycDecisionRow[] };
}>) {
  const sheet = useSheetActions();
  if (tab === 'decisions') {
    return (
      <DataTable
        id="verifications-decisions"
        columns={DECISION_COLUMNS}
        data={data.recent}
        empty="No decisions made here yet."
        rowLabel={(row) =>
          `Open the ID check for ${row.customer?.name ?? 'this customer'}`
        }
        onRowClick={(row, rowEl) =>
          sheet.open({ type: 'kyc', id: row.session_id }, { fromRowEl: rowEl })
        }
      />
    );
  }
  if (data.rows.length === 0) {
    return (
      <EmptyState
        variant="clear"
        title="No ID checks waiting."
        body={
          hasCapability('id_register')
            ? 'Margins/NIA checks are decided automatically.'
            : 'ID checks are decided automatically unless the provider asks for a person.'
        }
      />
    );
  }
  return (
    <DataTable
      id="verifications-review"
      columns={REVIEW_COLUMNS}
      data={data.rows}
      rowLabel={(row) => `Review ${row.customer?.name ?? 'this'} ID check`}
      onRowClick={(row, rowEl) =>
        sheet.open(
          { type: 'kyc', id: row.session_id },
          { fromRowEl: rowEl.querySelector('button') ?? rowEl },
        )
      }
    />
  );
}
